import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

import { devLog } from './jwt.constant';

export const CUSTOMER_ID_CLAIM = 'urn:asnbank:customer_id';
export const CONTEXT_ID_CLAIM = 'urn:asnbank:context_id';

@Injectable({ providedIn: 'root' })
export class MijnJwtService {
    private cachedToken: string | null = null;

    public isAuthenticated$: Observable<boolean> = of(false);
    public customerId$: Observable<string | null> = of(null);
    public contextId$: Observable<string | null> = of(null);

    /** @internal Used by AuthTokenSyncService only. Do not call from consumer code. */
    public setToken(token: string | null): void {
        devLog('setToken', token ? token.slice(-20) : null);
        this.cachedToken = token;
    }

    /** @internal Used by provideMijnJwtInterceptor only. Do not call from consumer code. */
    public setObservables(
        isAuthenticated$: Observable<boolean>,
        customerId$: Observable<string | null>,
        contextId$: Observable<string | null>
    ): void {
        this.isAuthenticated$ = isAuthenticated$;
        this.customerId$ = customerId$;
        this.contextId$ = contextId$;
    }

    /**
     * Returns the current valid token synchronously for use by HTTP and fetch interceptors.
     * Returns null if no token is set or the token is expired.
     */
    public getTokenForInterceptor(): string | null {
        return this.cachedToken && !this.isTokenExpired(this.cachedToken) ? this.cachedToken : null;
    }

    /** @deprecated Use the Observable API instead: subscribe to `isAuthenticated$`, `customerId$`, or `contextId$`. */
    public getToken(): string | null {
        return this.cachedToken;
    }

    /** @deprecated Use the Observable API instead: subscribe to `isAuthenticated$`, `customerId$`, or `contextId$`. */
    public getValidToken(): string | null {
        const token = this.getToken();
        return token && this.isTokenExpired(token) === false ? token : null;
    }

    /** @deprecated Use `isAuthenticated$` instead. */
    public isAuthenticated(): boolean {
        return this.getValidToken() !== null;
    }

    /** @deprecated Use `contextId$` instead. */
    public getContextId(): string | null {
        const token = this.getToken();
        if (!token) {
            return null;
        }
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload[CONTEXT_ID_CLAIM] ?? null;
        } catch {
            return null;
        }
    }

    /** @deprecated Use `customerId$` instead. */
    public getCustomerId(): string | null {
        const token = this.getToken();
        if (!token) {
            return null;
        }
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload[CUSTOMER_ID_CLAIM] ?? null;
        } catch {
            return null;
        }
    }

    private isTokenExpired(token: string): boolean {
        try {
            const { exp } = JSON.parse(atob(token.split('.')[1]));
            if (!exp) {
                return true;
            }
            return exp * 1000 <= Date.now();
        } catch {
            return true;
        }
    }
}
